// src/pages/tecaher/TeacherProfile.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container, Row, Col, Card, Button, Badge, Spinner,
  Form, Alert
} from "react-bootstrap";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import {
  collection, doc, addDoc, getDoc, getDocs, updateDoc, setDoc,
  query, where, limit, onSnapshot, serverTimestamp
} from "firebase/firestore";
import { ref as sRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../services/firebase";
import { useAuth } from "../../context/AuthContext";

/* ---------------- theme helpers ---------------- */
const cssVar = (name, fb = "") =>
  (typeof window !== "undefined" &&
    getComputedStyle(document.documentElement).getPropertyValue(name).trim()) || fb;

const useBrand = () => {
  const read = () => ({
    primary: cssVar("--brand-primary", "#0d6efd"),
    accent:  cssVar("--brand-accent",  "#6f42c1"),
    surface: cssVar("--surface", "#fff"),
  });
  const [brand, setBrand] = useState(read);
  useEffect(() => {
    const upd = () => setBrand(read());
    upd();
    const obs = new MutationObserver(upd);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-bs-theme","style","class"] });
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    mq?.addEventListener?.("change", upd);
    return () => { obs.disconnect(); mq?.removeEventListener?.("change", upd); };
  }, []);
  return brand;
};
const hexToRgba = (hex, a = 1) => {
  const h = String(hex || "").replace("#", "");
  const [r, g, b] = h.length === 3
    ? [parseInt(h[0]+h[0],16), parseInt(h[1]+h[1],16), parseInt(h[2]+h[2],16)]
    : [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
  return `rgba(${r||0},${g||0},${b||0},${a})`;
};

/* ---------------- fallbacks ---------------- */
const CLASS_FALLBACK = [
  ...Array.from({ length: 12 }, (_, i) => `Class ${i + 1}`),
  "Class 11", "Class 12", "Class 12 PCM", "Class 12 PCB", "Class 12 PCMB",
  "NEET", "JEE", "NEET Repeaters", "JEE Repeaters", "Crash Course",
  "Foundation 9", "Foundation 10",
];
const SECTION_FALLBACK = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
const BRANCH_FALLBACK = ["", "Science","Commerce","Arts","PCM","PCB","PCMB","CSE","IT","ECE","EEE"];

/* ---------------- utils ---------------- */
const lower = (s) => String(s || "").trim().toLowerCase();
const toU = (s) => String(s || "").trim().toUpperCase();

const parseList = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).map((x) => x.trim()).filter(Boolean);
  return String(raw).split(/[,\s/|]+/g).map((x) => x.trim()).filter(Boolean);
};
// keep “Class 12 PCM” intact — split only by comma/pipe/slash
const parseClassList = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).map(s=>s.trim()).filter(Boolean);
  return String(raw).split(/[,\|/]+/g).map(s=>s.trim()).filter(Boolean);
};
const getTeacherClasses = (t) => {
  const ids = parseClassList(t?.classIds);
  if (ids.length) return ids;
  const list = parseClassList(t?.classes || t?.classNames);
  if (list.length) return list;
  return t?.className ? [String(t.className)] : [];
};
const getSectionsForClass = (t, cls) => {
  if (!t) return [];
  if (t.sectionsByClass && t.sectionsByClass[cls]) return parseList(t.sectionsByClass[cls]).map(toU);
  const flat = parseList(t.sections).map(toU);
  if (flat.length) return flat;
  const single = parseList(t.section).map(toU);
  if (single.length) return single;
  return [];
};

/* ====== LOCKED FIELDS ====== */
const LOCKED_FIELDS = true;

/* =================================================================== */

export default function TeacherProfile() {
  const brand = useBrand();
  const { user, profile } = useAuth();
  const schoolId = profile?.schoolId || null;
  const teacherIdFromProfile = profile?.teacherId || null;

  // core
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);

  // active class/section (READ-ONLY selectors)
  const teacherClasses = useMemo(() => getTeacherClasses(teacher), [teacher]);
  const [activeClass, setActiveClass] = useState("");
  const [sectionsAvail, setSectionsAvail] = useState([]);
  const [activeSectionU, setActiveSectionU] = useState("");

  // form (non-locked fields are editable)
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    gender: "",
    primarySubject: "",
    subjects: "",
    className: "",   // shown but locked
    section: "",     // shown but locked
    branch: "",      // shown but locked
    defaultPlatform: "meet",
    defaultMeetingURL: "",
    bio: "",
    status: "active",
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [saving, setSaving] = useState(false);

  // stats
  const [stats, setStats] = useState({ students: 0, classes: 0, assignments: 0 });

  const fileRef = useRef(null);

  /* ---------------- load/resolve teacher ---------------- */
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!schoolId || !user?.uid) { setTeacher(null); setLoading(false); return; }
      setLoading(true);
      try {
        if (teacherIdFromProfile) {
          const snap = await getDoc(doc(db, "schools", schoolId, "teachers", teacherIdFromProfile));
          if (!cancelled && snap.exists()) { setTeacher({ id: snap.id, ...(snap.data()||{}) }); setLoading(false); return; }
        }
        const email = lower(user?.email || "");
        if (email) {
          const qs = await getDocs(query(
            collection(db, "schools", schoolId, "teachers"),
            where("email", "==", email),
            limit(1)
          ));
          if (!cancelled && !qs.empty) {
            const d = qs.docs[0];
            setTeacher({ id: d.id, ...(d.data()||{}) });
            setLoading(false);
            return;
          }
        }
        if (!cancelled) { setTeacher(null); setLoading(false); }
      } catch {
        if (!cancelled) { setTeacher(null); setLoading(false); }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [schoolId, user?.uid, user?.email, teacherIdFromProfile]);

  /* ---------------- hydrate form + active class/section ---------------- */
  useEffect(() => {
    setPhotoFile(null);
    setPhotoPreview("");
    if (!teacher) {
      setForm((f) => ({
        ...f,
        name: user?.displayName || "",
        email: lower(user?.email || ""),
      }));
      setActiveClass("");
      setSectionsAvail([]);
      setActiveSectionU("");
      return;
    }

    // form data
    setForm({
      name: teacher.name || user?.displayName || "",
      email: lower(teacher.email || user?.email || ""),
      phone: teacher.phone || "",
      gender: teacher.gender || "",
      primarySubject: teacher.primarySubject || "",
      subjects: Array.isArray(teacher.subjects) ? teacher.subjects.join(", ") : (teacher.subjects || ""),
      className: teacher.className || "", // legacy single — still shown
      section: teacher.section || "",
      branch: teacher.branch || "",
      defaultPlatform: teacher.defaultPlatform || "meet",
      defaultMeetingURL: teacher.defaultMeetingURL || "",
      bio: teacher.bio || "",
      status: teacher.status || "active",
    });
    setPhotoPreview(teacher.photoURL || "");

    // active class/section derived from multi-mapping
    const cls = getTeacherClasses(teacher);
    const first = cls[0] || teacher.className || "";
    setActiveClass(first);
    const secs = getSectionsForClass(teacher, first);
    setSectionsAvail(secs);
    setActiveSectionU(secs.includes(toU(teacher.section)) ? toU(teacher.section) : ""); // default to specific if matches
  }, [teacher, user?.displayName, user?.email]);

  // when class changes, refresh available sections
  useEffect(() => {
    const secs = getSectionsForClass(teacher, activeClass);
    setSectionsAvail(secs);
    if (secs.length && !secs.includes(activeSectionU)) setActiveSectionU("");
  }, [activeClass]); // eslint-disable-line

  /* ---------------- quick stats (live) ---------------- */
  useEffect(() => {
    if (!schoolId || !teacher) return;
    const unsubs = [];

    // students in selected class/section (use activeClass/activeSectionU)
    if (activeClass) {
      const col = collection(db, "schools", schoolId, "students");
      const q1 = activeSectionU
        ? query(col, where("className", "==", activeClass), where("section", "==", activeSectionU))
        : query(col, where("className", "==", activeClass));
      unsubs.push(onSnapshot(q1, (snap) => setStats((s) => ({ ...s, students: snap.size })) ));
    } else {
      setStats((s)=>({ ...s, students: 0 }));
    }

    // online classes (by teacher id/email/uid)
    const ids = new Set();
    const apply = () => setStats((s) => ({ ...s, classes: ids.size }));
    if (teacher.id) {
      unsubs.push(onSnapshot(
        query(collection(db, "schools", schoolId, "onlineClasses"), where("hostId", "==", teacher.id)),
        (snap) => { snap.docs.forEach(d => ids.add(d.id)); apply(); }
      ));
    }
    if (teacher.email) {
      unsubs.push(onSnapshot(
        query(collection(db, "schools", schoolId, "onlineClasses"), where("hostEmail", "==", lower(teacher.email)) ),
        (snap) => { snap.docs.forEach(d => ids.add(d.id)); apply(); }
      ));
    }
    if (user?.uid) {
      unsubs.push(onSnapshot(
        query(collection(db, "schools", schoolId, "onlineClasses"), where("createdBy.uid", "==", user.uid)),
        (snap) => { snap.docs.forEach(d => ids.add(d.id)); apply(); }
      ));
    }

    // assignments count (same as your original)
    if (teacher.id) {
      unsubs.push(onSnapshot(
        query(collection(db, "schools", schoolId, "assignments"), where("teacherId", "==", teacher.id)),
        (snap) => setStats((s)=>({ ...s, assignments: snap.size }))
      ));
    }

    return () => unsubs.forEach(u => u && u());
  }, [schoolId, teacher?.id, teacher?.email, user?.uid, activeClass, activeSectionU]);

  /* ---------------- options (display only, since locked) ---------------- */
  const classOptions = useMemo(() => {
    const u = new Set([...(teacherClasses || [])]);
    // include legacy single for visibility
    if (teacher?.className) u.add(teacher.className);
    return Array.from(new Set([...u, ...CLASS_FALLBACK]));
  }, [teacherClasses, teacher?.className]);

  const sectionOptions = useMemo(() => {
    return sectionsAvail.length ? sectionsAvail : SECTION_FALLBACK;
  }, [sectionsAvail]);

  /* ---------------- helpers ---------------- */
  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadAvatar = async (teacherDocId, file) => {
    if (!schoolId || !teacherDocId || !file) return { url: teacher?.photoURL || "", path: teacher?.photoPath || "" };
    let oldPath = teacher?.photoPath || "";
    try {
      if (oldPath) { try { await deleteObject(sRef(storage, oldPath)); } catch {} }
      const safe = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const path = `schools/${schoolId}/teachers/${teacherDocId}/${safe}`;
      const r = sRef(storage, path);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      return { url, path };
    } catch {
      return { url: teacher?.photoURL || "", path: oldPath };
    }
  };

  const save = async () => {
    if (!schoolId || !user?.uid) return;
    if (!form.name.trim()) return Swal.fire({ icon: "warning", title: "Name is required" });

    // LOCKED: persist exactly what’s on teacher doc; UI selectors are read-only
    const locked = {
      // keep legacy single fields for compatibility
      className: teacher?.className || "",
      section: teacher?.section || "",
      branch: teacher?.branch || "",
      primarySubject: teacher?.primarySubject || "",
      subjects: Array.isArray(teacher?.subjects) ? teacher?.subjects :
                (teacher?.subjects ? String(teacher?.subjects).split(",").map(s=>s.trim()).filter(Boolean) : []),

      // also mirror multi-mapping if present (no edits here)
      classIds: Array.isArray(teacher?.classIds) ? teacher.classIds : undefined,
      classes: Array.isArray(teacher?.classes) ? teacher.classes : undefined,
      classNames: Array.isArray(teacher?.classNames) ? teacher.classNames : undefined,
      sectionsByClass: teacher?.sectionsByClass || undefined,
      sections: Array.isArray(teacher?.sections) ? teacher.sections : undefined,
    };

    setSaving(true);
    try {
      let teacherDocId = teacher?.id;
      let payloadBase = {
        name: form.name.trim(),
        email: form.email || lower(user?.email || ""),
        phone: form.phone.trim(),
        gender: form.gender || "",
        ...locked,
        defaultPlatform: form.defaultPlatform || "meet",
        defaultMeetingURL: form.defaultMeetingURL.trim(),
        bio: form.bio.trim(),
        status: form.status || "active",
        updatedAt: serverTimestamp(),
        userUid: user?.uid || "",
      };

      // CREATE if needed
      if (!teacherDocId) {
        const newRef = await addDoc(collection(db, "schools", schoolId, "teachers"), {
          ...payloadBase,
          createdAt: serverTimestamp(),
        });
        teacherDocId = newRef.id;
        try { await setDoc(doc(db, "users", user.uid), { teacherId: teacherDocId }, { merge: true }); } catch {}
      }

      // avatar
      let avatar = { url: teacher?.photoURL || "", path: teacher?.photoPath || "" };
      if (photoFile) avatar = await uploadAvatar(teacherDocId, photoFile);

      const finalPayload = { ...payloadBase, photoURL: avatar.url, photoPath: avatar.path };
      await setDoc(doc(db, "schools", schoolId, "teachers", teacherDocId), finalPayload, { merge: true });

      // mirror to /users
      try {
        await setDoc(doc(db, "users", user.uid), {
          displayName: finalPayload.name,
          email: finalPayload.email,
          photoURL: finalPayload.photoURL,
          className: locked.className,
          section: locked.section,
          teacherId: teacherDocId,
        }, { merge: true });
      } catch {}

      Swal.fire({ icon: "success", title: "Profile saved", timer: 1100, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed to save", text: e?.message || "Unexpected error" });
    } finally {
      setSaving(false);
    }
  };

  const removePhoto = async () => {
    if (!schoolId || !teacher?.id) return;
    const ok = await Swal.fire({
      icon: "warning",
      title: "Remove photo?",
      showCancelButton: true,
      confirmButtonText: "Remove",
      confirmButtonColor: "#d33",
    });
    if (!ok.isConfirmed) return;
    try {
      if (teacher.photoPath) { try { await deleteObject(sRef(storage, teacher.photoPath)); } catch {} }
      await updateDoc(doc(db, "schools", schoolId, "teachers", teacher.id), {
        photoURL: "", photoPath: "", updatedAt: serverTimestamp(),
      });
      setPhotoPreview("");
      setTeacher((t) => t ? { ...t, photoURL: "", photoPath: "" } : t);
      Swal.fire({ icon: "success", title: "Removed", timer: 900, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Couldn’t remove", text: e?.message || "Storage error" });
    }
  };

  // Request profile change → writes to /schools/{id}/requests
  const requestChange = async () => {
    if (!schoolId) return;
    const { value: msg } = await Swal.fire({
      title: "Request profile changes",
      input: "textarea",
      inputPlaceholder: "Describe what you want updated (e.g., add Class 10 sections A/B, set branch PCM, change primary to Physics)…",
      inputAttributes: { "aria-label": "Request details" },
      showCancelButton: true,
      confirmButtonText: "Send request",
      confirmButtonColor: "#0d6efd",
    });
    if (!msg) return;
    try {
      await addDoc(collection(db, "schools", schoolId, "requests"), {
        type: "teacherProfileChange",
        teacherId: teacher?.id || null,
        teacherEmail: lower(teacher?.email || user?.email || ""),
        teacherName: form.name || user?.displayName || "",
        current: {
          classIds: teacher?.classIds || null,
          classes: teacher?.classes || teacher?.classNames || null,
          className: teacher?.className || "",
          sectionsByClass: teacher?.sectionsByClass || null,
          sections: teacher?.sections || null,
          section: teacher?.section || "",
          branch: teacher?.branch || "",
          primarySubject: teacher?.primarySubject || "",
          subjects: Array.isArray(teacher?.subjects) ? teacher.subjects : String(teacher?.subjects || ""),
        },
        message: msg,
        status: "open",
        createdAt: serverTimestamp(),
        createdByUid: user?.uid || "",
      });
      Swal.fire({ icon: "success", title: "Request sent to admin", timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Couldn’t send request", text: e?.message || "Permission denied" });
    }
  };

  /* ---------------- UI ---------------- */
  if (!schoolId) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">No school selected.</Card.Body>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> Loading profile…
        </Card.Body>
      </Card>
    );
  }

  const teacherHasMulti = teacherClasses.length > 1 || Object.keys(teacher?.sectionsByClass || {}).length > 0;

  return (
    <Container fluid className="p-0">
      {/* Header / hero */}
      <div className="tp-hero mb-3">
        <div className="tp-hero-inner">
          <div className="d-flex align-items-center gap-3">
            <div className="avatar-wrap">
              <div className="avatar" style={{
                backgroundImage: `url(${photoPreview || teacher?.photoURL || ""})`
              }}>
                {!photoPreview && !teacher?.photoURL ? <i className="bi bi-person" /> : null}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setPhotoFile(f);
                  setPhotoPreview(f ? URL.createObjectURL(f) : (teacher?.photoURL || ""));
                }}
              />
            </div>

            <div>
              <div className="tdb-eyebrow">Teacher • Profile</div>
              <div className="h5 fw-bold mb-1">{form.name || "Your Name"}</div>
              <div className="d-flex flex-wrap align-items-center gap-2 text-muted">
                <Badge bg="light" text="dark">
                  Class {activeClass || form.className || "—"}{activeSectionU ? `-${activeSectionU}` : form.section ? `-${form.section}` : ""}
                </Badge>
                {form.primarySubject ? <Badge bg="light" text="dark">{form.primarySubject}</Badge> : null}
                {form.branch ? <Badge bg="light" text="dark">Branch {form.branch}</Badge> : null}
                <Badge bg={form.status === "active" ? "success" : "secondary"}>
                  {String(form.status || "active").toUpperCase()}
                </Badge>
              </div>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <Button variant="outline-secondary" onClick={()=>fileRef.current?.click()}>
              <i className="bi bi-image me-1" /> {photoPreview || teacher?.photoURL ? "Change photo" : "Upload photo"}
            </Button>
            {(teacher?.photoURL || photoPreview) ? (
              <Button variant="outline-danger" onClick={removePhoto}>
                <i className="bi bi-trash me-1" /> Remove photo
              </Button>
            ) : null}
            <Button variant="outline-primary" onClick={requestChange}>
              <i className="bi bi-chat-dots me-1" /> Request change
            </Button>
            <Button className="btn-accent" onClick={save} disabled={saving}>
              {saving ? (<><Spinner size="sm" animation="border" className="me-1" />Saving…</>) : (<><i className="bi bi-save me-1" /> Save</>)}
            </Button>
          </div>
        </div>
      </div>

      {!teacher && (
        <Alert variant="info" className="mb-3">
          We couldn’t find your teacher profile. You can create one now (class/section/branch & subjects are set by admin).
        </Alert>
      )}

      {/* Quick stats */}
      <Row className="g-3 mb-3">
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100"><Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="small text-muted">Students</div>
                <div className="h5 mb-0">{stats.students}</div>
              </div>
              <div className="stat-ico"><i className="bi bi-people" /></div>
            </div>
          </Card.Body></Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100"><Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="small text-muted">Online Classes</div>
                <div className="h5 mb-0">{stats.classes}</div>
              </div>
              <div className="stat-ico"><i className="bi bi-camera-video" /></div>
            </div>
          </Card.Body></Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100"><Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="small text-muted">Assignments</div>
                <div className="h5 mb-0">{stats.assignments}</div>
              </div>
              <div className="stat-ico"><i className="bi bi-clipboard-check" /></div>
            </div>
          </Card.Body></Card>
        </Col>
      </Row>

      {/* Profile form */}
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Alert className="py-2">
            <i className="bi bi-lock-fill me-2" />
            <strong>Class/Section/Branch & Subjects are managed by the school admin.</strong>
            &nbsp;Use “Request change” to ask for updates.
          </Alert>

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Name *</Form.Label>
                <Form.Control value={form.name} onChange={(e)=>change("name", e.target.value)} placeholder="Your full name" required />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Email</Form.Label>
                <Form.Control value={form.email} onChange={(e)=>change("email", lower(e.target.value))} placeholder="email@example.com" />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Phone</Form.Label>
                <Form.Control disabled={LOCKED_FIELDS}value={form.phone} onChange={(e)=>change("phone", e.target.value)} placeholder="+91 9xxxxxxxxx" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Gender</Form.Label>
                <Form.Select value={form.gender} disabled={LOCKED_FIELDS}onChange={(e)=>change("gender", e.target.value)}>
                  <option value="">—</option>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select value={form.status}disabled={LOCKED_FIELDS} onChange={(e)=>change("status", e.target.value)}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {/* MULTI-CLASS/SECTION (READ-ONLY) */}
            <Col md={4}>
              <Form.Group>
                <Form.Label>Class (mapped)</Form.Label>
                <Form.Select
                  value={activeClass}
                  onChange={(e)=>setActiveClass(e.target.value)}
                  disabled={LOCKED_FIELDS}
                  readOnly
                >
                  {(teacherClasses.length ? teacherClasses : [form.className || ""]).filter(Boolean).map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                  {!teacherClasses.length && !form.className ? <option value="">—</option> : null}
                </Form.Select>
                {teacherHasMulti && (
                  <div className="small text-muted mt-1">
                    {teacherClasses.length} class{teacherClasses.length>1?"es":""} mapped
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Section (for selected class)</Form.Label>
                <Form.Select
                  value={activeSectionU}
                  onChange={(e)=>setActiveSectionU(toU(e.target.value))}
                  disabled={LOCKED_FIELDS}
                  readOnly
                >
                  <option value="">All Sections</option>
                  {sectionOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Branch</Form.Label>
                <Form.Select value={form.branch} disabled={LOCKED_FIELDS} readOnly>
                  {BRANCH_FALLBACK.map(b => <option key={b || "ALL"} value={b}>{b || "—"}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Primary Subject</Form.Label>
                <Form.Control value={form.primarySubject} disabled={LOCKED_FIELDS} readOnly />
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group>
                <Form.Label>Other Subjects</Form.Label>
                <Form.Control value={form.subjects} disabled={LOCKED_FIELDS} readOnly />
              </Form.Group>
            </Col>

            {/* Non-locked */}
            <Col md={4}>
              <Form.Group>
                <Form.Label>Default Platform</Form.Label>
                <Form.Select value={form.defaultPlatform} onChange={(e)=>change("defaultPlatform", e.target.value)}>
                  <option value="meet">Google Meet</option>
                  <option value="zoom">Zoom</option>
                  <option value="teams">Microsoft Teams</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group>
                <Form.Label>Default Meeting Link</Form.Label>
                <Form.Control
                  value={form.defaultMeetingURL}
                  onChange={(e)=>change("defaultMeetingURL", e.target.value)}
                  type="url"
                  placeholder="https://meet.google.com/..."
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group>
                <Form.Label>About / Bio</Form.Label>
                <Form.Control
                  as="textarea" rows={4}
                  value={form.bio}
                  onChange={(e)=>change("bio", e.target.value)}
                  placeholder="Short introduction, experience, achievements etc."
                />
              </Form.Group>
            </Col>

            <Col xs={12} className="d-flex justify-content-end gap-2 mt-2">
              <Button variant="outline-primary" onClick={requestChange}>
                <i className="bi bi-chat-dots me-1" /> Request change
              </Button>
              <Button className="btn-accent" onClick={save} disabled={saving}>
                {saving ? (<><Spinner size="sm" animation="border" className="me-1" />Saving…</>) : (<><i className="bi bi-save me-1" /> Save</>)}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* styles */}
      <style>{`
        .tp-hero {
          border: 1px solid var(--border);
          border-radius: 16px;
          background:
            radial-gradient(60% 120% at 10% -20%, ${hexToRgba(brand.accent, .16)} 0%, transparent 60%),
            radial-gradient(40% 120% at 110% 10%, ${hexToRgba(brand.accent, .10)} 0%, transparent 60%),
            color-mix(in oklab, var(--surface) 96%, transparent);
        }
        .tp-hero-inner { display:flex; align-items:center; justify-content:space-between; padding: 14px 16px; border-radius: 16px; }
        .tdb-eyebrow { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: var(--bs-secondary-color); }

        .avatar-wrap { position: relative; }
        .avatar {
          width: 80px; height: 80px; border-radius: 20px; overflow: hidden;
          background: ${hexToRgba(brand.primary, .1)};
          border: 1px solid ${hexToRgba(brand.primary, .25)};
          display:flex; align-items:center; justify-content:center; color: ${brand.primary};
          background-size: cover; background-position: center;
          font-size: 36px;
        }

        .stat-ico {
          width: 44px; height: 44px; border-radius: 12px;
          display:flex; align-items:center; justify-content:center;
          background: ${hexToRgba(brand.primary, .12)};
          color: ${brand.primary};
          border: 1px solid ${hexToRgba(brand.primary, .30)};
          font-size: 22px;
        }

        .btn-accent {
          --accent: ${brand.accent};
          background-color: var(--accent) !important;
          border-color: var(--accent) !important;
          color: #fff !important;
          box-shadow: 0 .25rem .6rem ${hexToRgba(brand.accent, .35)};
        }
        .btn-accent:hover, .btn-accent:focus { filter: brightness(0.92); color: #fff !important; }
      `}</style>
    </Container>
  );
}
